import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  ArrowsClockwise, CurrencyBtc, ArrowUp, ArrowDown, ArrowSquareOut,
  FunnelSimple, CaretLeft, CaretRight, Dog, Cube, MagnifyingGlass
} from "@phosphor-icons/react";

function fmt(val) {
  if (!val || val === 0) return "$0";
  if (val >= 1e12) return `$${(val / 1e12).toFixed(2)}T`;
  if (val >= 1e9) return `$${(val / 1e9).toFixed(2)}B`;
  if (val >= 1e6) return `$${(val / 1e6).toFixed(1)}M`;
  if (val >= 1e3) return `$${(val / 1e3).toFixed(0)}K`;
  return `$${val.toFixed(2)}`;
}

function fmtPrice(val) {
  if (!val || val === 0) return "$0";
  if (val >= 1000) return `$${val.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (val >= 1) return `$${val.toFixed(2)}`;
  if (val >= 0.001) return `$${val.toFixed(4)}`;
  if (val >= 0.0000001) return `$${val.toFixed(8)}`;
  return `$${val.toExponential(2)}`;
}

function PctChange({ val }) {
  if (val === null || val === undefined) return <span className="text-zinc-600">-</span>;
  const isUp = val >= 0;
  return (
    <span className={`text-[10px] font-data flex items-center justify-end gap-0.5 ${isUp ? "text-emerald-400" : "text-red-400"}`}>
      {isUp ? <ArrowUp size={8} weight="bold" /> : <ArrowDown size={8} weight="bold" />}
      {Math.abs(val).toFixed(1)}%
    </span>
  );
}

function LiqGauge({ score }) {
  const color = score >= 60 ? "emerald" : score >= 30 ? "amber" : "red";
  return (
    <div className="flex items-center gap-1.5" title={`Liquidity: ${score}/100`}>
      <div className="w-12 bg-zinc-800/60 rounded-full h-1.5 overflow-hidden">
        <div className={`h-full rounded-full bg-${color}-500/70`} style={{ width: `${Math.min(score, 100)}%` }} />
      </div>
      <span className={`text-[9px] font-data text-${color}-400`}>{score}</span>
    </div>
  );
}

function TokenRow({ token, onClick }) {
  const isMeme = token.category === "meme";
  return (
    <button
      data-testid={`token-row-${token.rank}`}
      onClick={onClick}
      className="w-full text-left border-b border-zinc-800/30 hover:bg-zinc-900/40 transition-colors"
    >
      {/* Desktop: full grid */}
      <div className="hidden md:grid grid-cols-[40px_1.4fr_100px_110px_100px_70px_70px_70px_90px] items-center gap-2 px-3 py-2.5">
        <span className="text-[10px] font-data text-zinc-600 text-center">{token.rank}</span>

        <div className="flex items-center gap-2 min-w-0">
          {token.image ? (
            <img src={token.image} alt={token.symbol} className="w-6 h-6 rounded-full flex-shrink-0" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
              <CurrencyBtc size={12} className="text-zinc-500" />
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-zinc-100 truncate">{token.name}</span>
              <Badge className={`text-[6px] px-1 py-0 border-0 rounded-sm h-3 ${
                isMeme ? "bg-amber-900/30 text-amber-400" : "bg-blue-900/30 text-blue-400"
              }`}>
                {isMeme ? "MEME" : "UTILITY"}
              </Badge>
            </div>
            <span className="text-[9px] text-zinc-500 font-data">{token.symbol}</span>
          </div>
        </div>

        <span className="text-[11px] font-data text-zinc-200 text-right">{fmtPrice(token.current_price)}</span>
        <span className="text-[11px] font-data text-zinc-300 text-right">{fmt(token.market_cap)}</span>
        <span className="text-[10px] font-data text-zinc-400 text-right">{fmt(token.total_volume)}</span>

        <PctChange val={token.price_change_1h} />
        <PctChange val={token.price_change_24h} />
        <PctChange val={token.price_change_7d} />

        <div className="flex justify-end">
          <LiqGauge score={token.liquidity_score} />
        </div>
      </div>

      {/* Mobile: compact card */}
      <div className="md:hidden px-3 py-2.5">
        <div className="flex items-center gap-2.5 mb-2">
          <span className="text-[10px] font-data text-zinc-600 w-6 shrink-0 text-right">{token.rank}</span>
          {token.image ? (
            <img src={token.image} alt={token.symbol} className="w-7 h-7 rounded-full flex-shrink-0" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
              <CurrencyBtc size={14} className="text-zinc-500" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="text-[13px] font-medium text-zinc-100 truncate">{token.symbol}</span>
              <Badge className={`text-[6px] px-1 py-0 border-0 rounded-sm h-3 ${
                isMeme ? "bg-amber-900/30 text-amber-400" : "bg-blue-900/30 text-blue-400"
              }`}>
                {isMeme ? "MEME" : "UTIL"}
              </Badge>
            </div>
            <span className="text-[10px] text-zinc-500 truncate block">{token.name}</span>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[12px] font-data font-medium text-zinc-100">{fmtPrice(token.current_price)}</div>
            <PctChange val={token.price_change_24h} />
          </div>
        </div>
        <div className="grid grid-cols-4 gap-1.5 pl-[38px]">
          <div>
            <span className="text-[8px] uppercase text-zinc-600 block leading-tight">Mcap</span>
            <span className="text-[10px] font-data text-zinc-300">{fmt(token.market_cap)}</span>
          </div>
          <div>
            <span className="text-[8px] uppercase text-zinc-600 block leading-tight">Vol 24h</span>
            <span className="text-[10px] font-data text-zinc-400">{fmt(token.total_volume)}</span>
          </div>
          <div>
            <span className="text-[8px] uppercase text-zinc-600 block leading-tight">1h / 7d</span>
            <div className="flex items-center gap-1">
              <PctChange val={token.price_change_1h} />
              <span className="text-zinc-700 text-[9px]">·</span>
              <PctChange val={token.price_change_7d} />
            </div>
          </div>
          <div className="flex flex-col items-end">
            <span className="text-[8px] uppercase text-zinc-600 block leading-tight">Liq</span>
            <LiqGauge score={token.liquidity_score} />
          </div>
        </div>
      </div>
    </button>
  );
}

export default function TokenList() {
  const navigate = useNavigate();
  const [pages, setPages] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [websites, setWebsites] = useState({});

  const fetchPage = useCallback(async (pg, silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const resp = await axios.get(`${API}/tokens?page=${pg}`);
      setPages(prev => ({ ...prev, [pg]: resp.data }));
    } catch (e) {
      console.error("Failed to fetch tokens:", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchPage(currentPage); }, [currentPage, fetchPage]);

  const data = pages[currentPage];
  const allTokens = data?.tokens || [];

  const filtered = allTokens.filter(t => {
    if (filter === "meme" && t.category !== "meme") return false;
    if (filter === "utility" && t.category !== "utility") return false;
    if (search) {
      const q = search.toLowerCase();
      return t.name.toLowerCase().includes(q) || t.symbol.toLowerCase().includes(q);
    }
    return true;
  });

  const memeCount = allTokens.filter(t => t.category === "meme").length;
  const utilCount = allTokens.filter(t => t.category === "utility").length;

  const rangeStart = (currentPage - 1) * 250 + 1;
  const rangeEnd = rangeStart + 249;

  return (
    <div className="p-3 md:p-5 max-w-[1400px]" data-testid="tokens-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-4 md:mb-5 gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 md:gap-2.5 mb-1">
            <CurrencyBtc size={20} weight="bold" className="text-amber-400 shrink-0" />
            <h1 className="font-heading text-lg md:text-2xl font-bold text-zinc-50 tracking-tight">Tokens</h1>
            <Badge className="text-[8px] px-1.5 py-0.5 border-0 bg-zinc-800 text-zinc-400 h-4">TOP 3000</Badge>
          </div>
          <p className="text-[10px] md:text-xs text-zinc-500 md:ml-[34px] hidden sm:block">
            Ranked by market cap with volume, liquidity scores, and category filters
          </p>
        </div>
        <button
          data-testid="refresh-tokens-btn"
          onClick={() => fetchPage(currentPage, true)}
          disabled={refreshing}
          className="flex items-center gap-1.5 text-[10px] text-zinc-400 hover:text-zinc-200 transition-colors px-2.5 py-1.5 border border-zinc-800 rounded-sm shrink-0"
        >
          <ArrowsClockwise size={12} className={refreshing ? "animate-spin" : ""} />
          <span className="hidden sm:inline">{refreshing ? "Updating..." : "Refresh"}</span>
        </button>
      </div>

      {/* Summary Cards */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="tokens-summary">
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3">
            <span className="text-[9px] uppercase tracking-wider text-zinc-500">Showing</span>
            <p className="text-base font-data font-bold text-zinc-100 mt-0.5">#{rangeStart} - #{rangeEnd}</p>
          </div>
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3">
            <span className="text-[9px] uppercase tracking-wider text-zinc-500">On This Page</span>
            <p className="text-base font-data font-bold text-zinc-100 mt-0.5">{allTokens.length} tokens</p>
          </div>
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3">
            <span className="text-[9px] uppercase tracking-wider text-amber-500/80 flex items-center gap-1"><Dog size={10} /> Memes</span>
            <p className="text-base font-data font-bold text-amber-400 mt-0.5">{memeCount}</p>
          </div>
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3">
            <span className="text-[9px] uppercase tracking-wider text-blue-500/80 flex items-center gap-1"><Cube size={10} /> Utilities</span>
            <p className="text-base font-data font-bold text-blue-400 mt-0.5">{utilCount}</p>
          </div>
        </div>
      )}

      {/* Filter + Search */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2" data-testid="token-controls">
        <div className="flex items-center gap-1.5 md:gap-2 overflow-x-auto scrollbar-hide">
          <FunnelSimple size={12} className="text-zinc-600 shrink-0" />
          {[
            { key: "all", label: "All", count: allTokens.length },
            { key: "meme", label: "Memes", count: memeCount },
            { key: "utility", label: "Utility", count: utilCount },
          ].map(f => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`text-[10px] px-2.5 py-1 rounded-sm border transition-colors whitespace-nowrap shrink-0 ${
                filter === f.key
                  ? "bg-zinc-800 text-zinc-100 border-zinc-600"
                  : "bg-zinc-900/30 text-zinc-500 border-zinc-800/60 hover:text-zinc-300"
              }`}
            >
              {f.label} ({f.count})
            </button>
          ))}
        </div>
        <div className="relative">
          <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input
            data-testid="token-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter tokens..."
            className="text-[10px] pl-7 pr-3 py-1.5 bg-zinc-900/50 border border-zinc-800/60 rounded-sm text-zinc-300 placeholder:text-zinc-600 outline-none focus:border-zinc-600 w-full sm:w-[180px]"
          />
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="space-y-1">
          {[...Array(15)].map((_, i) => <Skeleton key={i} className="h-10 bg-zinc-900/50" />)}
        </div>
      )}

      {/* Table */}
      {!loading && filtered.length > 0 && (
        <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-sm overflow-hidden" data-testid="tokens-table">
          {/* Header (desktop only) */}
          <div className="hidden md:grid grid-cols-[40px_1.4fr_100px_110px_100px_70px_70px_70px_90px] items-center gap-2 px-3 py-2 border-b border-zinc-800/60 bg-zinc-900/50">
            <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-center">#</span>
            <span className="text-[8px] uppercase tracking-wider text-zinc-600">Token</span>
            <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">Price</span>
            <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">Market Cap</span>
            <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">24h Vol</span>
            <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">1h</span>
            <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">24h</span>
            <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">7d</span>
            <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">Liquidity</span>
          </div>

          {/* Rows */}
          <div style={{ maxHeight: "calc(100vh - 340px)", overflowY: "auto" }} data-testid="tokens-scroll">
            {filtered.map(token => (
              <TokenRow
                key={`${token.symbol}-${token.chain}-${token.rank}`}
                token={token}
                onClick={() => navigate(`/asset/crypto/${encodeURIComponent(token.id)}`)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty */}
      {!loading && filtered.length === 0 && (
        <div className="text-center py-16">
          <p className="text-sm text-zinc-500">No tokens match your filter</p>
        </div>
      )}

      {/* Pagination */}
      {!loading && (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mt-4 pt-3 border-t border-zinc-800/40">
          <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide pb-1 md:flex-wrap md:pb-0">
            {Array.from({ length: 12 }, (_, i) => i + 1).map(pg => (
              <button
                key={pg}
                data-testid={`page-${pg}`}
                onClick={() => setCurrentPage(pg)}
                className={`text-[10px] px-2.5 py-1.5 rounded-sm border transition-colors whitespace-nowrap shrink-0 ${
                  currentPage === pg
                    ? "bg-zinc-700 text-zinc-100 border-zinc-600"
                    : "bg-zinc-900/30 text-zinc-500 border-zinc-800/60 hover:text-zinc-300"
                }`}
              >
                {(pg - 1) * 250 + 1}-{pg * 250}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 justify-between md:justify-end">
            <button
              data-testid="prev-page"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              className="text-[10px] px-2 py-1.5 rounded-sm border border-zinc-800/60 text-zinc-500 hover:text-zinc-300 disabled:opacity-30 transition-colors"
            >
              <CaretLeft size={12} />
            </button>
            <span className="text-[10px] text-zinc-500 whitespace-nowrap">Page {currentPage} of 12</span>
            <button
              data-testid="next-page"
              disabled={currentPage === 12}
              onClick={() => setCurrentPage(p => Math.min(12, p + 1))}
              className="text-[10px] px-2 py-1.5 rounded-sm border border-zinc-800/60 text-zinc-500 hover:text-zinc-300 disabled:opacity-30 transition-colors"
            >
              <CaretRight size={12} />
            </button>
          </div>
          {data && (
            <p className="text-[9px] md:text-[10px] text-zinc-600 text-center md:text-right flex items-center gap-1.5 justify-center md:justify-end flex-wrap">
              {data?.stale && (
                <span className="text-amber-400/80 bg-amber-500/10 border border-amber-500/25 rounded-sm px-1.5 py-0.5 text-[9px]" data-testid="tokens-stale-badge">
                  Cached data — upstream rate-limited
                </span>
              )}
              <span>Source: {data?.data_source || "On-chain"} | Updated: {new Date(data.fetched_at).toLocaleString()}</span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
